'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Logo } from '@/components/brand/logo'
import { Search, Loader2, ArrowLeft, CheckCircle, Clock, XCircle, AlertCircle, FileText, Lock, Unlock, Download } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { format } from 'date-fns'

interface ApplicationStatus {
  id: string
  enterprise_name: string
  candidate_name: string
  father_or_husband_name: string | null
  email: string
  phone: string | null
  status: string
  created_at: string
  notes: string | null
  category_of_enterprise: string | null
  location_type: string | null
}

interface PDF {
  id: string
  original_filename: string
  file_size: number
  is_locked: boolean
  unlock_code: string
  created_at: string
}

export default function TrackStatusPage() {
  const router = useRouter()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [searchType, setSearchType] = useState<'token' | 'details'>('token')
  const [tokenNumber, setTokenNumber] = useState('')
  const [candidateName, setCandidateName] = useState('')
  const [fatherName, setFatherName] = useState('')
  const [aadhaarNumber, setAadhaarNumber] = useState('')
  const [application, setApplication] = useState<ApplicationStatus | null>(null)
  const [pdfs, setPdfs] = useState<PDF[]>([])
  const [loadingPdfs, setLoadingPdfs] = useState(false)

  const handleSearchByToken = async () => {
    if (!tokenNumber.trim()) {
      toast({
        title: 'Required',
        description: 'Please enter your token number',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)
    try {
      // Extract submission ID from token (remove # and convert to lowercase)
      const submissionId = tokenNumber.replace('#', '').toLowerCase().trim()

      console.log('Searching for token:', submissionId)
      console.log('Original token input:', tokenNumber)

      const response = await fetch(`/api/track-status?token=${submissionId}`)

      console.log('Track status response:', response.status, response.ok)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Track status error:', errorData)
        throw new Error('Application not found')
      }

      const data = await response.json()
      console.log('Found application:', data.application?.id)
      setApplication(data.application)

      // Fetch PDFs for this submission
      await fetchPDFs(data.application.id)
    } catch (error: any) {
      console.error('Token search error:', error)
      toast({
        title: 'Not Found',
        description: 'No application found with this token number. Please check and try again.',
        variant: 'destructive',
        duration: 10000,
      })
      setApplication(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearchByDetails = async () => {
    if (!candidateName.trim() || (!fatherName.trim() && !aadhaarNumber.trim())) {
      toast({
        title: 'Required',
        description: 'Please enter candidate name and either father\'s name or Aadhaar number',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)
    try {
      const params = new URLSearchParams({
        candidate_name: candidateName,
      })

      if (fatherName) params.append('father_name', fatherName)
      if (aadhaarNumber) params.append('aadhaar', aadhaarNumber)

      const response = await fetch(`/api/track-status?${params}`)

      if (!response.ok) {
        throw new Error('Application not found')
      }

      const data = await response.json()
      setApplication(data.application)

      // Fetch PDFs for this submission
      await fetchPDFs(data.application.id)
    } catch (error: any) {
      toast({
        title: 'Not Found',
        description: 'No application found with these details. Please check and try again.',
        variant: 'destructive',
      })
      setApplication(null)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPDFs = async (submissionId: string) => {
    setLoadingPdfs(true)
    try {
      const response = await fetch(`/api/customer/pdfs?submission_id=${submissionId}`)

      if (!response.ok) {
        throw new Error('Failed to fetch PDFs')
      }

      const data = await response.json()
      setPdfs(data.data || [])
    } catch (error) {
      // Silently fail for PDFs - not critical
      setPdfs([])
    } finally {
      setLoadingPdfs(false)
    }
  }

  const handleDownloadPDF = async (pdfId: string, filename: string, unlockCode: string) => {
    try {
      const response = await fetch(`/api/customer/pdfs/download?id=${pdfId}&unlock_code=${unlockCode}`)

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to download document')
      }

      const data = await response.json()

      if (data.success && data.data.download_url) {
        // Open download URL in new tab
        window.open(data.data.download_url, '_blank')

        toast({
          title: 'Success',
          description: 'Document is being downloaded',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Download Failed',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <Clock className="h-5 w-5 text-blue-500" />
      case 'contacted':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />
      case 'qualified':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'lost':
        return <XCircle className="h-5 w-5 text-red-500" />
      default:
        return <Clock className="h-5 w-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-300'
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-300'
      case 'qualified':
        return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-300'
      case 'lost':
        return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-300'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-900 dark:text-gray-300'
    }
  }

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'new':
        return 'Your application has been received and is under review.'
      case 'contacted':
        return 'We have reached out to you. Please check your email/phone.'
      case 'qualified':
        return 'Congratulations! Your application has been qualified.'
      case 'lost':
        return 'Unfortunately, we cannot proceed with your application at this time.'
      default:
        return 'Your application is being processed.'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted-50 to-muted-100 dark:from-ink-900 dark:to-ink-800">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-display">Track Application Status</CardTitle>
            <CardDescription className="text-lg">
              Check the status of your application using your token number or personal details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Search Type Tabs */}
            <div className="flex gap-2 p-1 bg-muted rounded-lg">
              <Button
                variant={searchType === 'token' ? 'default' : 'ghost'}
                className="flex-1"
                onClick={() => setSearchType('token')}
              >
                Search by Token
              </Button>
              <Button
                variant={searchType === 'details' ? 'default' : 'ghost'}
                className="flex-1"
                onClick={() => setSearchType('details')}
              >
                Search by Details
              </Button>
            </div>

            {/* Search by Token */}
            {searchType === 'token' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="token">Token Number</Label>
                  <div className="flex gap-2">
                    <Input
                      id="token"
                      placeholder="Enter your token number (e.g., #A1B2C3D4)"
                      value={tokenNumber}
                      onChange={(e) => setTokenNumber(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchByToken()}
                      className="font-mono"
                    />
                    <Button
                      onClick={handleSearchByToken}
                      disabled={isLoading}
                      className="bg-brand-500 hover:bg-brand-600"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter the token number you received after submitting your application
                  </p>
                </div>
              </div>
            )}

            {/* Search by Details */}
            {searchType === 'details' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="candidateName">Candidate Name *</Label>
                  <Input
                    id="candidateName"
                    placeholder="Enter candidate name"
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fatherName">Father/Husband Name</Label>
                  <Input
                    id="fatherName"
                    placeholder="Enter father/husband name"
                    value={fatherName}
                    onChange={(e) => setFatherName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="aadhaar">Aadhaar Number (Optional)</Label>
                  <Input
                    id="aadhaar"
                    placeholder="Enter Aadhaar number"
                    value={aadhaarNumber}
                    onChange={(e) => setAadhaarNumber(e.target.value)}
                    maxLength={12}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter either father&apos;s name or Aadhaar number to search
                  </p>
                </div>

                <Button
                  onClick={handleSearchByDetails}
                  disabled={isLoading}
                  className="w-full bg-brand-500 hover:bg-brand-600"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      Search Application
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Application Status Display */}
            {application && (
              <div className="mt-8 space-y-4 border-t pt-6">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-semibold mb-2">Application Found!</h3>
                  <p className="text-muted-foreground">Here&apos;s the current status of your application</p>
                </div>

                <div className="bg-muted/50 p-6 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(application.status)}
                      <div>
                        <p className="text-sm text-muted-foreground">Current Status</p>
                        <Badge className={getStatusColor(application.status)} variant="outline">
                          {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Submitted On</p>
                      <p className="font-medium">
                        {format(new Date(application.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm text-muted-foreground mb-2">Status Message</p>
                    <p className="font-medium">{getStatusMessage(application.status)}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 border-t pt-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Enterprise Name</p>
                      <p className="font-medium">{application.enterprise_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Candidate Name</p>
                      <p className="font-medium">{application.candidate_name}</p>
                    </div>
                    {application.category_of_enterprise && (
                      <div>
                        <p className="text-sm text-muted-foreground">Category</p>
                        <p className="font-medium">{application.category_of_enterprise}</p>
                      </div>
                    )}
                    {application.location_type && (
                      <div>
                        <p className="text-sm text-muted-foreground">Location Type</p>
                        <p className="font-medium">{application.location_type}</p>
                      </div>
                    )}
                  </div>

                  {application.notes && (
                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground mb-2">Admin Notes</p>
                      <div className="bg-white dark:bg-ink-900 p-3 rounded border">
                        <p className="text-sm whitespace-pre-wrap">{application.notes}</p>
                      </div>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <p className="text-sm text-muted-foreground mb-1">Token Number</p>
                    <p className="font-mono font-medium">
                      #{application.id.substring(0, 8).toUpperCase()}
                    </p>
                  </div>

                  {/* PDF Documents Section */}
                  {pdfs.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="text-lg font-semibold mb-3">Your Documents</h4>
                      <div className="space-y-3">
                        {pdfs.map((pdf) => (
                          <Card key={pdf.id} className="p-4 bg-white dark:bg-ink-900">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <FileText className="h-5 w-5 text-brand-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-medium truncate">{pdf.original_filename}</h5>
                                    <p className="text-xs text-muted-foreground">
                                      {(pdf.file_size / 1024).toFixed(2)} KB
                                    </p>
                                  </div>
                                  <Badge
                                    className={
                                      pdf.is_locked
                                        ? 'bg-red-100 text-red-800 border-red-300'
                                        : 'bg-green-100 text-green-800 border-green-300'
                                    }
                                    variant="outline"
                                  >
                                    {pdf.is_locked ? (
                                      <>
                                        <Lock className="h-3 w-3 mr-1" />
                                        Locked
                                      </>
                                    ) : (
                                      <>
                                        <Unlock className="h-3 w-3 mr-1" />
                                        Unlocked
                                      </>
                                    )}
                                  </Badge>
                                </div>

                                {pdf.is_locked ? (
                                  <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                                    <p className="text-xs text-yellow-800 dark:text-yellow-200">
                                      🔒 This document is currently locked. You can download it once the admin unlocks it.
                                    </p>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => handleDownloadPDF(pdf.id, pdf.original_filename, pdf.unlock_code)}
                                    className="mt-2 bg-brand-500 hover:bg-brand-600"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                  </Button>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
